import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";
import { useRampState } from "../../../../containers/RampStateContainer";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { useUserPreferences } from "../../../../containers/UserPreferencesContainer";
import { Ramp } from "../../../../types";
import { ConfirmationDialog } from "../../../../widgets/ConfirmationDialog";
import { RampCanvas, Shape } from "./RampCanvas/RampCanvas";
import { RampEditorForm } from "./RampEditorForm";
import { useAutoEgress } from "./hooks/useAutoEgress";

interface RampEditorState {
  showReferenceImage: boolean;
  setShowReferenceImage: (show: boolean) => void;
  showMeasurements: boolean;
  setShowMeasurements: (show: boolean) => void;
  showGuideLines: boolean;
  setShowGuideLines: (show: boolean) => void;
  showCalibrationPoints: boolean;
  setShowCalibrationPoints: (show: boolean) => void;
  highlightedCalibrationPoint: string | null;
  setHighlightedCalibrationPoint: (pointId: string | null) => void;
  useV2: boolean;
  setUseV2: (useV2: boolean) => void;
}

export const useRampEditorState = create<RampEditorState>(
  (set): RampEditorState => ({
    showReferenceImage: true,
    setShowReferenceImage: (showReferenceImage: boolean) =>
      set({ showReferenceImage }),
    showMeasurements: true,
    setShowMeasurements: (showMeasurements: boolean) =>
      set({ showMeasurements }),
    showGuideLines: true,
    setShowGuideLines: (showGuideLines: boolean) => set({ showGuideLines }),
    showCalibrationPoints: true,
    setShowCalibrationPoints: (showCalibrationPoints: boolean) =>
      set({ showCalibrationPoints }),
    highlightedCalibrationPoint: null,
    setHighlightedCalibrationPoint: (
      highlightedCalibrationPoint: string | null
    ) => set({ highlightedCalibrationPoint }),
    useV2: false,
    setUseV2: (useV2: boolean) => set({ useV2 }),
  })
);

type Props = {
  onClose: () => void;
};

interface LocalRampState {
  ramp: Ramp | undefined;
  setRamp: (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => void;
  //
  activeShapes: string[];
  setActiveShapes: (activeShapes: string[]) => void;
  getActiveShapes: () => Shape[];
  //
  highlightedShapes: string[];
  setHighlightedShapes: (highlightedShapes: string[]) => void;
  //
  reset: () => void;
}

export const useLocalRampState = create<LocalRampState>(
  (set, get): LocalRampState => ({
    ramp: undefined,
    setRamp: (rampOrUpdater: Ramp | ((prevRamp: Ramp) => Ramp)) => {
      if (typeof rampOrUpdater === "function") {
        set((state) => {
          if (!state.ramp) {
            console.error(
              "[useLocalRampState] Cannot use functional update when ramp is undefined"
            );
            return state;
          }
          return { ramp: rampOrUpdater(state.ramp) };
        });
      } else {
        set({ ramp: rampOrUpdater });
      }
    },
    //
    activeShapes: [],
    setActiveShapes: (activeShapes: string[]) => set({ activeShapes }),
    getActiveShapes() {
      return get().ramp.shapes.filter((s) => get().activeShapes.includes(s.id));
    },
    //
    highlightedShapes: [],
    setHighlightedShapes: (highlightedShapes: string[]) =>
      set({ highlightedShapes }),
    //
    reset: () =>
      set({
        ramp: undefined,
        activeShapes: [],
        highlightedShapes: [],
      }),
  })
);

export const useSelectedToolState = create<{
  selectedTool: string;
  setSelectedTool: (tool: string) => void;
}>((set) => ({
  selectedTool: "Select",
  setSelectedTool: (selectedTool: string) => set({ selectedTool }),
}));

export const useCalibrationState = create<{
  isAddingCalibrationPoint: boolean;
  setIsAddingCalibrationPoint: (adding: boolean) => void;
}>((set) => ({
  isAddingCalibrationPoint: false,
  setIsAddingCalibrationPoint: (isAddingCalibrationPoint: boolean) =>
    set({ isAddingCalibrationPoint }),
}));

export const RampEditorPresenter: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ramp, setRamp } = useRampState();
  const { setRamp: setLocalRamp, ramp: localRamp } = useLocalRampState();
  const { selectedTool, setSelectedTool } = useSelectedToolState();
  const snackbar = useSnackbar();
  const { preferences, setPreferences } = useUserPreferences();
  const { showGuideLines } = useRampEditorState();
  const hasLoadedInitialPreferences = React.useRef(false);

  React.useEffect(() => {
    // Only set local ramp if we have a valid ramp from the container
    if (ramp && ramp.id) {
      setLocalRamp(ramp);
    }
  }, [ramp]);

  // Load initial preference once
  React.useEffect(() => {
    if (
      !hasLoadedInitialPreferences.current &&
      preferences.showGuideLines !== undefined
    ) {
      useRampEditorState
        .getState()
        .setShowGuideLines(preferences.showGuideLines);
      hasLoadedInitialPreferences.current = true;
    }
  }, [preferences.showGuideLines]);

  // Save preference when it changes (but not on initial load)
  React.useEffect(() => {
    if (hasLoadedInitialPreferences.current) {
      setPreferences((currentPreferences) => ({
        ...currentPreferences,
        showGuideLines,
      }));
    }
  }, [showGuideLines, setPreferences]);

  // Auto-calculate egress whenever shapes change (debounced)
  useAutoEgress();

  // State for exit confirmation dialog (Cancel button)
  const [showExitConfirmation, setShowExitConfirmation] = React.useState(false);

  // Check if there are unsaved changes by comparing localRamp with original ramp
  const hasUnsavedChanges = React.useMemo((): boolean => {
    if (!ramp || !localRamp) return false;

    // Compare shapes
    if (JSON.stringify(ramp.shapes) !== JSON.stringify(localRamp.shapes)) return true;

    // Compare markings
    if (JSON.stringify(ramp.markings) !== JSON.stringify(localRamp.markings)) return true;

    // Compare calibration_points
    if (JSON.stringify(ramp.calibration_points) !== JSON.stringify(localRamp.calibration_points)) return true;

    // Compare ramp properties
    if (ramp.name !== localRamp.name) return true;
    if (ramp.width !== localRamp.width) return true;
    if (ramp.depth !== localRamp.depth) return true;
    if (ramp.reference_image !== localRamp.reference_image) return true;

    return false;
  }, [ramp, localRamp]);

  // State for browser back button confirmation
  const [showBackConfirmation, setShowBackConfirmation] = React.useState(false);
  const isHandlingBack = React.useRef(false);

  // Block browser back button / swipe navigation when there are unsaved changes
  React.useEffect(() => {
    // Push a state to detect back navigation
    const currentHash = window.location.hash;
    window.history.pushState({ rampEditorGuard: true }, "", currentHash);

    const handlePopState = (event: PopStateEvent) => {
      if (isHandlingBack.current) return;

      // Check unsaved changes via Zustand store (avoids stale closure)
      const currentLocalRamp = useLocalRampState.getState().ramp;
      const currentRamp = ramp;

      const hasChanges = (() => {
        if (!currentRamp || !currentLocalRamp) return false;
        if (JSON.stringify(currentRamp.shapes) !== JSON.stringify(currentLocalRamp.shapes)) return true;
        if (JSON.stringify(currentRamp.markings) !== JSON.stringify(currentLocalRamp.markings)) return true;
        if (JSON.stringify(currentRamp.calibration_points) !== JSON.stringify(currentLocalRamp.calibration_points)) return true;
        if (currentRamp.name !== currentLocalRamp.name) return true;
        if (currentRamp.width !== currentLocalRamp.width) return true;
        if (currentRamp.depth !== currentLocalRamp.depth) return true;
        if (currentRamp.reference_image !== currentLocalRamp.reference_image) return true;
        return false;
      })();

      if (hasChanges) {
        // Push state back to prevent navigation, then show dialog
        window.history.pushState({ rampEditorGuard: true }, "", currentHash);
        setShowBackConfirmation(true);
      }
      // If no changes, allow the back navigation to proceed naturally
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [ramp]);

  // Handler for confirming back navigation
  const confirmBackNavigation = () => {
    isHandlingBack.current = true;
    setShowBackConfirmation(false);
    // Go back twice: once for the guard state we pushed, once for actual back
    window.history.go(-2);
  };

  // Handle page refresh / tab close with beforeunload
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Guarded exit handler for Cancel button and back arrow
  const handleExit = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirmation(true);
    } else {
      navigate("/settings?tab=Ramps");
    }
  };

  // Confirm exit (discard changes)
  const confirmExit = () => {
    setShowExitConfirmation(false);
    navigate("/settings?tab=Ramps");
  };

  const onClickSaveRamp = () => {
    setRamp(localRamp);
    // setRamps(ramps.map((r) => (r.id === localRamp.id ? localRamp : r)));
    snackbar.notify({ text: t('settings.rampsSettings.rampEditor.saveSuccess'), severity: "success" });
  };
  /**
   * make a ramp
   * give it a name
   * upload an image
   * make a reference line to tell us how many pixels = how many feet
   * add an outline
   * add other markings
   */

  if (!localRamp) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={8}
      alignItems="flex-start"
      width="100%"
      height="100%"
    >
      <Box
        sx={{
          p: 1,
          width: 280,
          // position: "sticky", // Makes the Box sticky
          // top: 0, // Stick to the top of the parent container
          alignSelf: "flex-start", // Align properly within the parent
          maxHeight: "100%",
          overflowY: "auto",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <IconButton onClick={handleExit}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle2">{t('settings.rampsSettings.rampEditor.title')}</Typography>
          </Stack>
        </Stack>
        <RampEditorForm
          ramp={localRamp}
          setRamp={setLocalRamp}
          onClickSaveRamp={onClickSaveRamp}
          onClose={handleExit}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
        />
      </Box>
      <Stack
        direction="column"
        spacing={1}
        width="100%"
        sx={{
          overflowY: "auto", // Allow vertical scrolling for this section
          maxHeight: "100vh", // Limit height to viewport for scrollable content
        }}
      >
        <RampCanvas
          ramp={localRamp}
          setRamp={setLocalRamp}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
        />
      </Stack>

      {/* Confirmation dialog for Cancel button / back arrow */}
      {showExitConfirmation && (
        <ConfirmationDialog
          title={t('settings.rampsSettings.rampEditor.unsavedChanges.title')}
          text={t('settings.rampsSettings.rampEditor.unsavedChanges.message')}
          okText={t('settings.rampsSettings.rampEditor.unsavedChanges.exitButton')}
          cancelText={t('settings.rampsSettings.rampEditor.unsavedChanges.stayButton')}
          onOk={confirmExit}
          onCancel={() => setShowExitConfirmation(false)}
          onClose={() => setShowExitConfirmation(false)}
        />
      )}

      {/* Confirmation dialog for browser back button / swipe */}
      {showBackConfirmation && (
        <ConfirmationDialog
          title={t('settings.rampsSettings.rampEditor.unsavedChanges.title')}
          text={t('settings.rampsSettings.rampEditor.unsavedChanges.message')}
          okText={t('settings.rampsSettings.rampEditor.unsavedChanges.exitButton')}
          cancelText={t('settings.rampsSettings.rampEditor.unsavedChanges.stayButton')}
          onOk={confirmBackNavigation}
          onCancel={() => setShowBackConfirmation(false)}
          onClose={() => setShowBackConfirmation(false)}
        />
      )}
    </Stack>
  );
};
